export const MODES = ['sale', 'free', 'swap', 'barter'] as const
// Display order. The first eight ids are the original categories and keep their meaning.
export const CATEGORIES = ['home', 'kitchen', 'appliances', 'electronics', 'tools', 'building', 'plants', 'sports', 'water', 'vehicles', 'clothing', 'kids', 'pets', 'books', 'collectibles', 'other'] as const
// market_items.category is CHECK-limited to the original eight, so each newer id is stored
// beside a broad legacy parent in that column and itself in category_key.
export const LEGACY_PARENT: Record<typeof CATEGORIES[number], 'home' | 'appliances' | 'plants' | 'sports' | 'tools' | 'collectibles' | 'electronics' | 'other'> = {
  home: 'home', kitchen: 'home', appliances: 'appliances', electronics: 'electronics', tools: 'tools', building: 'tools', plants: 'plants', sports: 'sports',
  water: 'sports', vehicles: 'other', clothing: 'other', kids: 'other', pets: 'other', books: 'other', collectibles: 'collectibles', other: 'other',
}
export const CONDITIONS = ['like-new', 'good', 'used', 'repair'] as const
export const AREAS = ['Isabel II', 'Esperanza', 'Other Vieques'] as const
export const PICKUPS = ['public', 'arrange', 'vehicle'] as const
export type Mode = typeof MODES[number]
export type ItemStatus = 'draft' | 'pending' | 'published' | 'rejected' | 'archived' | 'completed'
export interface ItemInput {
  title: string
  description: string
  category: typeof CATEGORIES[number]
  mode: Mode
  priceCents: number | null
  condition: typeof CONDITIONS[number]
  area: typeof AREAS[number]
  pickup: typeof PICKUPS[number]
  exchange: string
}
/** Photos of a sold item are deleted this many days after it is marked exchanged; the seller is warned first. */
export const SOLD_PHOTO_DAYS = 30
export const PHOTO_NOTICE_DAYS = 7
/** A listing this old gets a nudge with ways to help it sell. */
export const STALE_LEVELS = [{ level: 1, days: 180, months: 6 }, { level: 2, days: 365, months: 12 }] as const
export interface Item extends ItemInput {
  id: string
  version: number
  status: ItemStatus
  availability: 'available' | 'reserved'
  photo: string | null
  /** Up to three more photos, by place: index 0 is extra photo 1. An empty place is null. */
  photos?: (string | null)[]
  seller: string
  demo: boolean
  mine: boolean
  /** Private to the seller: when the exchange was marked complete, and when its photos will be deleted. */
  completedAt?: number | null
  boosted?: boolean
  /** The seller says a sale price is open to offers (only ever set on a sale). */
  acceptsOffers?: boolean
  /** A frame the seller bought for this listing (see shared/market/frames.ts). */
  frame?: string
  boostedUntil?: number | null
  photosExpireAt?: number | null
  saved: boolean
  createdAt: number
  updatedAt: number
  moderationNote?: string
  photoDeletionPending?: boolean
}
export function validateItem(value: unknown): { fields: ItemInput | null; errors: string[] } {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return { fields: null, errors: ['body'] }
  const x = value as Record<string, unknown>
  const keys = ['title','description','category','mode','priceCents','condition','area','pickup','exchange']
  const errors: string[] = []
  if (Object.keys(x).some(k => !keys.includes(k))) errors.push('unknown_field')
  const string = (k: string) => typeof x[k] === 'string' ? x[k].trim() : ''
  const title = string('title'), description = string('description'), exchange = string('exchange')
  if (title.length < 3 || title.length > 90) errors.push('title')
  if (description.length < 20 || description.length > 1200) errors.push('description')
  for (const [key, options] of [['category', CATEGORIES], ['mode', MODES], ['condition', CONDITIONS], ['area', AREAS], ['pickup', PICKUPS]] as const) {
    if (!(options as readonly unknown[]).includes(x[key])) errors.push(key)
  }
  if (x.mode === 'sale' && (!Number.isInteger(x.priceCents) || Number(x.priceCents) < 1 || Number(x.priceCents) > 9999900)) errors.push('priceCents')
  if ((x.mode === 'swap' || x.mode === 'barter') && (exchange.length < 5 || exchange.length > 300)) errors.push('exchange')
  if (x.mode === 'sale' && exchange.length > 0 && exchange.length < 5) errors.push('exchange') // a sale that is also open to barter says what it would take
  if (exchange.length > 300) errors.push('exchange')
  // Public ads never become an address book. Human review also checks for an
  // exact pickup address or personal information that cannot be recognized here.
  const prose = [title, description, exchange].join(' ')
  if (/[\w.+-]+@[\w.-]+\.[a-z]{2,}/i.test(prose) || /(?:\+?1[- .]?)?\(?\d{3}\)?[- .]\d{3}[- .]\d{4}/.test(prose) || /\b\d{1,6}\s+[\p{L} .'-]{1,50}\b(?:street|avenue|road|calle|carretera|st\.|ave\.)/iu.test(prose)) errors.push('public_contact')
  if (Array.from(prose).some(char => { const code = char.charCodeAt(0); return code < 32 && ![9,10,13].includes(code) })) errors.push('text')
  if (errors.length) return { fields: null, errors }
  return { fields: { title, description, category: x.category as ItemInput['category'], mode: x.mode as Mode, priceCents: x.mode === 'sale' ? Number(x.priceCents) : null, condition: x.condition as ItemInput['condition'], area: x.area as ItemInput['area'], pickup: x.pickup as ItemInput['pickup'], exchange: x.mode === 'swap' || x.mode === 'barter' || x.mode === 'sale' ? exchange : '' }, errors: [] }
}
export function normalize(value: string): string { return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim() }
export function matches(item: Item, query: string): boolean { const content = normalize([item.title,item.description,item.category,item.area,item.exchange].join(' ')); return normalize(query).split(/\s+/).filter(Boolean).every(token => content.includes(token)) }

/** Trust coconuts: one for every "yes, that was the person" from a neighbor. Levels are fun names for the count. */
export const COCONUT_LEVELS = [
  { level: 0, min: 0, key: 'seed' },
  { level: 1, min: 1, key: 'sprout' },
  { level: 2, min: 5, key: 'palmita' },
  { level: 3, min: 15, key: 'cocotero' },
  { level: 4, min: 40, key: 'palmar' },
] as const
export function coconutLevel(n: number): { level: number; key: string; next: number | null } {
  let cur: (typeof COCONUT_LEVELS)[number] = COCONUT_LEVELS[0]
  for (const l of COCONUT_LEVELS) if (n >= l.min) cur = l
  const nxt = COCONUT_LEVELS.find(l => l.min > n)
  return { level: cur.level, key: cur.key, next: nxt ? nxt.min : null }
}

/** Island creatures that earn simulated tokens. A parrot is worth one more than a coqui; a palm is the big one. */
export type Creature = 'coqui' | 'parrot' | 'palm'
export const TOKEN_VALUE: Record<Creature, number> = { coqui: 1, parrot: 2, palm: 5 }

/** Meeting spots. A spot is trusted for everyone once this many different neighbors vote for it. */
export const TRUSTED_SPOT_VOTES = 5
/** Spots closer than this (metres) are the same spot. */
export const SPOT_RADIUS_M = 60
/** Locations can only be shared on or near Vieques. */
export const VIEQUES_BOX = { latMin: 17.95, latMax: 18.35, lngMin: -65.7, lngMax: -65.1 } as const
export function metresBetween(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const R = 6371000, rad = (d: number) => (d * Math.PI) / 180
  const dLat = rad(bLat - aLat), dLng = rad(bLng - aLng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}
export function inVieques(lat: number, lng: number): boolean {
  return Number.isFinite(lat) && Number.isFinite(lng) && lat >= VIEQUES_BOX.latMin && lat <= VIEQUES_BOX.latMax && lng >= VIEQUES_BOX.lngMin && lng <= VIEQUES_BOX.lngMax
}
